import React from 'react'
import { useRouter } from 'next/router'
import { Box, Flex, Text } from '@fundamentals'
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import LaunchIcon from '@mui/icons-material/Launch'
import { ModalDialogContainer } from '@common/modal'
import { useModal } from '@common/GlobalModal'
import { localizedStrings } from '@core/strings'
import { PageUrls } from '@core/page-urls'
import { PermitBase } from '@core/entities/permit/PermitBase'
import { BlockingChild } from '@core/entities/permit/Permit'

type Props = {
  parentPermit: PermitBase
  blockingChildren: Array<BlockingChild>
}

export const CloseBlockedByChildrenModal: React.FC<Props> = ({
  parentPermit,
  blockingChildren,
}) => {
  const { hideModal } = useModal()
  const router = useRouter()

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.cannotClosePermit}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Text variant='body2' sx={{ mb: 2 }}>
          {localizedStrings.cannotClosePermitWithOpenChildren(
            blockingChildren.length,
          )}
        </Text>
        {blockingChildren.map((child) => (
          <Flex
            key={child.id}
            alignItems='center'
            justifyContent='space-between'
            sx={{
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Text variant='body2' fontWeight={500}>
                {child.templateName} — {child.code ?? child.id}
              </Text>
              <Text variant='caption' color='text.secondary'>
                {child.name}
              </Text>
            </Box>
            <Flex alignItems='center' gap={1}>
              <Chip
                size='small'
                label={PermitBase.getStatusString(child.status)}
                color={PermitBase.getStatusChipColor(child.status)}
              />
              <IconButton
                size='small'
                onClick={() => {
                  hideModal()
                  router.push(
                    PageUrls.projectPermitDetail({
                      organisationId: parentPermit.organisationId,
                      projectId: parentPermit.project?.id,
                      permitId: child.id,
                    }),
                  )
                }}
                data-test={`blocking-child-link-${child.id}`}
              >
                <LaunchIcon fontSize='small' />
              </IconButton>
            </Flex>
          </Flex>
        ))}
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='contained' onClick={hideModal}>
          {localizedStrings.ok}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
